import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Lightbulb, Video, Mic, Sparkles, Globe, ChevronDown, AlertTriangle } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useUserCredits } from '@/hooks/useUserCredits';
import { useInfluencerAccess } from '@/hooks/useInfluencerAccess';
import { VoiceInput } from '@/components/voice/VoiceInput';
import { VideoRecorder } from '@/components/video/VideoRecorder';
import { JacKeyGuideV2 } from '@/components/wizard/JacKeyGuideV2';
import { MessageToneTools } from '@/components/wizard/MessageToneTools';
import { StartForMe } from '@/components/wizard/StartForMe';
import { MessageLanguageSelector, type TranslationLanguage } from '@/components/wizard/MessageLanguageSelector';
import { INTENT_DATA, resolveIntentSlug, lstr, lstrArr } from '@/components/wizard/jackey-intents';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useEventTracking } from '@/hooks/useEventTracking';
import { moderateContent, type ModerationResult } from '@/types/courage';
import type { AnnouncementFormData } from '@/types/announcement';


const msgCopy = {
  en: { title: 'Write Your Message', subtitle: "Pour your heart out. This is your chance to say what you've always wanted to say to", them: 'them', placeholder: 'Write from the heart. There are no wrong words here.', editNote: 'This is just a starting place. Feel free to change anything so it sounds exactly like you.', videoAttached: 'Video message attached', remove: 'Remove', hidePrompts: 'Hide prompts', quickInspo: 'Quick inspiration', recordVideo: 'Record Video', speakToType: 'Speak to type in text box', questionsTitle: 'Questions to inspire you:', starterTitle: 'Try a sentence starter:', videoToast: 'Video message attached!', crushTitle: 'Write Your Secret Note', crushSubtitle: "Say what you've been holding inside.", crushSafety: 'Messages must be respectful and sincere.', crushPlaceholder: "I've wanted to tell you this for a long time…", needHelp: 'Need help getting started?' },
  es: { title: 'Escribe Tu Mensaje', subtitle: 'Abre tu corazón. Esta es tu oportunidad de decir lo que siempre quisiste decirle a', them: 'esa persona', placeholder: 'Escribe desde el corazón. No hay palabras incorrectas aquí.', editNote: 'Esto es solo un punto de partida. Siéntete libre de cambiar lo que quieras.', videoAttached: 'Video adjunto', remove: 'Eliminar', hidePrompts: 'Ocultar', quickInspo: 'Inspiración rápida', recordVideo: 'Grabar Video', speakToType: 'Habla para escribir', questionsTitle: 'Preguntas para inspirarte:', starterTitle: 'Prueba una frase inicial:', videoToast: '¡Video adjunto!', crushTitle: 'Escribe Tu Nota Secreta', crushSubtitle: 'Di lo que has guardado dentro.', crushSafety: 'Los mensajes deben ser respetuosos y sinceros.', crushPlaceholder: 'He querido decirte esto hace mucho tiempo…', needHelp: '¿Necesitas ayuda para empezar?' },
  fr: { title: 'Écrivez Votre Message', subtitle: "Ouvrez votre cœur. C'est votre chance de dire ce que vous avez toujours voulu dire à", them: 'cette personne', placeholder: "Écrivez avec le cœur. Il n'y a pas de mauvais mots ici.", editNote: "Ceci n'est qu'un point de départ. N'hésitez pas à modifier pour que ça vous ressemble.", videoAttached: 'Vidéo jointe', remove: 'Supprimer', hidePrompts: 'Masquer', quickInspo: 'Inspiration rapide', recordVideo: 'Enregistrer Vidéo', speakToType: 'Parlez pour saisir du texte', questionsTitle: 'Questions pour vous inspirer :', starterTitle: 'Essayez une phrase de départ :', videoToast: 'Vidéo jointe !', crushTitle: 'Écrivez Votre Note Secrète', crushSubtitle: 'Dites ce que vous gardez au fond de vous.', crushSafety: 'Les messages doivent être respectueux et sincères.', crushPlaceholder: 'Je voulais te dire ça depuis longtemps…', needHelp: 'Besoin d\'aide pour commencer ?' },
  pt: { title: 'Escreva Sua Mensagem', subtitle: 'Abra seu coração. Esta é sua chance de dizer o que sempre quis dizer para', them: 'essa pessoa', placeholder: 'Escreva de coração. Não existem palavras erradas aqui.', editNote: 'Isso é só um ponto de partida. Sinta-se livre para mudar o que quiser.', videoAttached: 'Vídeo anexado', remove: 'Remover', hidePrompts: 'Ocultar', quickInspo: 'Inspiração rápida', recordVideo: 'Gravar Vídeo', speakToType: 'Fale para digitar', questionsTitle: 'Perguntas para inspirar:', starterTitle: 'Tente uma frase inicial:', videoToast: 'Vídeo anexado!', crushTitle: 'Escreva Sua Nota Secreta', crushSubtitle: 'Diga o que você tem guardado.', crushSafety: 'As mensagens devem ser respeitosas e sinceras.', crushPlaceholder: 'Eu quero te dizer isso há muito tempo…', needHelp: 'Precisa de ajuda para começar?' },
  ar: { title: 'اكتب رسالتك', subtitle: 'افتح قلبك. هذه فرصتك لقول ما طالما أردت قوله لـ', them: 'هذا الشخص', placeholder: 'اكتب من القلب. لا توجد كلمات خاطئة هنا.', editNote: 'هذه مجرد نقطة بداية. لا تتردد في تعديلها لتعبر عنك.', videoAttached: 'فيديو مرفق', remove: 'إزالة', hidePrompts: 'إخفاء', quickInspo: 'إلهام سريع', recordVideo: 'تسجيل فيديو', speakToType: 'تحدث للكتابة', questionsTitle: 'أسئلة لإلهامك:', starterTitle: 'جرب عبارة للبدء:', videoToast: 'تم إرفاق الفيديو!', crushTitle: 'اكتب ملاحظتك السرية', crushSubtitle: 'قل ما كنت تحتفظ به بداخلك.', crushSafety: 'يجب أن تكون الرسائل محترمة وصادقة.', crushPlaceholder: 'أردت أن أقول لك هذا منذ وقت طويل…', needHelp: 'هل تحتاج مساعدة للبدء؟' },
  ja: { title: 'メッセージを書く', subtitle: '心の内を打ち明けましょう。ずっと伝えたかったことを伝えるチャンスです：', them: 'その人', placeholder: '心のままに書いてください。間違った言葉はありません。', editNote: 'これは出発点です。あなたらしく自由に変更してください。', videoAttached: '動画メッセージ添付済み', remove: '削除', hidePrompts: '非表示', quickInspo: 'インスピレーション', recordVideo: '動画を録画', speakToType: '音声入力', questionsTitle: 'インスピレーションの質問：', starterTitle: '文の書き出しを試す：', videoToast: '動画メッセージが添付されました！', crushTitle: '秘密のメモを書く', crushSubtitle: 'ずっと心に秘めていたことを伝えましょう。', crushSafety: 'メッセージは敬意と誠意を持って書いてください。', crushPlaceholder: 'ずっと伝えたかったことがあります…', needHelp: '書き始めに手助けが必要ですか？' },
  ko: { title: '메시지를 작성하세요', subtitle: '진심을 담아 써보세요. 항상 전하고 싶었던 말을 전할 기회입니다:', them: '그 사람', placeholder: '진심으로 써주세요. 틀린 말은 없습니다.', editNote: '이것은 시작점일 뿐입니다. 당신답게 자유롭게 수정하세요.', videoAttached: '영상 메시지 첨부됨', remove: '제거', hidePrompts: '숨기기', quickInspo: '빠른 영감', recordVideo: '영상 녹화', speakToType: '음성으로 입력', questionsTitle: '영감을 주는 질문:', starterTitle: '시작 문구를 시도하세요:', videoToast: '영상 메시지가 첨부되었습니다！', crushTitle: '비밀 메모를 작성하세요', crushSubtitle: '마음속에 담아둔 말을 전해보세요.', crushSafety: '메시지는 존중하고 진심어린 것이어야 합니다.', crushPlaceholder: '오래전부터 전하고 싶었던 말이 있어요…', needHelp: '시작하는 데 도움이 필요하세요?' },
  id: { title: 'Tulis Pesan Anda', subtitle: 'Curahkan hati Anda. Ini kesempatan untuk mengatakan apa yang selalu ingin Anda katakan kepada', them: 'mereka', placeholder: 'Tulis dari hati. Tidak ada kata yang salah di sini.', editNote: 'Ini hanya titik awal. Silakan ubah agar terdengar seperti Anda.', videoAttached: 'Pesan video terlampir', remove: 'Hapus', hidePrompts: 'Sembunyikan', quickInspo: 'Inspirasi cepat', recordVideo: 'Rekam Video', speakToType: 'Bicara untuk mengetik', questionsTitle: 'Pertanyaan untuk menginspirasi:', starterTitle: 'Coba frasa pembuka:', videoToast: 'Pesan video terlampir!', crushTitle: 'Tulis Catatan Rahasia Anda', crushSubtitle: 'Katakan apa yang selama ini Anda pendam.', crushSafety: 'Pesan harus sopan dan tulus.', crushPlaceholder: 'Saya sudah ingin mengatakan ini sejak lama…', needHelp: 'Butuh bantuan untuk memulai?' },
};

const INTENT_LABELS: Record<string, Record<string, string>> = {
  apology: { en: 'Apology', es: 'Disculpa', fr: 'Excuses', pt: 'Desculpas', ar: 'اعتذار', ja: '謝罪', ko: '사과', id: 'Permintaan Maaf' },
  gratitude: { en: 'Gratitude', es: 'Gratitud', fr: 'Gratitude', pt: 'Gratidão', ar: 'امتنان', ja: '感謝', ko: '감사', id: 'Rasa Terima Kasih' },
  hard_conversation: { en: 'Hard Conversation', es: 'Conversación Difícil', fr: 'Conversation Difficile', pt: 'Conversa Difícil', ar: 'محادثة صعبة', ja: '難しい会話', ko: '어려운 대화', id: 'Percakapan Sulit' },
  love: { en: 'Love', es: 'Amor', fr: 'Amour', pt: 'Amor', ar: 'حب', ja: '愛', ko: '사랑', id: 'Cinta' },
  closure: { en: 'Closure', es: 'Cierre', fr: 'Clôture', pt: 'Encerramento', ar: 'إغلاق', ja: '区切り', ko: '마무리', id: 'Penutupan' },
  custom: { en: 'Personal', es: 'Personal', fr: 'Personnel', pt: 'Pessoal', ar: 'شخصي', ja: '個人', ko: '개인', id: 'Pribadi' },
  secret_admire: { en: 'Secret Admiration', es: 'Admiración Secreta', fr: 'Admiration Secrète', pt: 'Admiração Secreta', ar: 'إعجاب سري', ja: '密かな想い', ko: '비밀 동경', id: 'Kekaguman Rahasia' },
  birthday: { en: 'Birthday', es: 'Cumpleaños', fr: 'Anniversaire', pt: 'Aniversário', ar: 'عيد ميلاد', ja: '誕生日', ko: '생일', id: 'Ulang Tahun' },
  anniversary: { en: 'Anniversary', es: 'Aniversario', fr: 'Anniversaire de couple', pt: 'Aniversário de relacionamento', ar: 'ذكرى سنوية', ja: '記念日', ko: '기념일', id: 'Hari Jadi' },
  celebration: { en: 'Celebration', es: 'Celebración', fr: 'Célébration', pt: 'Celebração', ar: 'احتفال', ja: 'お祝い', ko: '축하', id: 'Perayaan' },
};

interface MessageStepProps {
  formData: AnnouncementFormData;
  updateFormData: (updates: Partial<AnnouncementFormData>) => void;
}

export function MessageStep({ formData, updateFormData }: MessageStepProps) {
  const { language } = useLanguage();
  const c = lc(msgCopy, language);
  const { trackEvent } = useEventTracking();
  const isCrush = formData.relationship === 'crush';

  // Resolve intent
  const intentSlug = resolveIntentSlug(formData.template, formData.relationship);
  const intentConfig = INTENT_DATA[intentSlug];
  const intentLabel = INTENT_LABELS[intentSlug]?.[language] || INTENT_LABELS[intentSlug]?.en || intentSlug;
  const sentenceStarters = lstrArr(intentConfig.sentenceStarters, language);
  const intentQuestions = lstrArr(intentConfig.questions, language);
  const unlockPromptText = lstr(intentConfig.unlockPrompt, language);

  const [showPrompts, setShowPrompts] = useState(false);
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState<string | null>(null);
  const [jacKeyDismissed, setJacKeyDismissed] = useState(false);
  const [showUnlockPrompt, setShowUnlockPrompt] = useState(false);

  // Restore video URL from metadata when returning to this step
  useEffect(() => {
    const savedUrl = (formData.metadata as any)?.videoUrl;
    if (savedUrl && !recordedVideoUrl) {
      setRecordedVideoUrl(savedUrl);
    }
  }, []);

  // Language selection state
  const [writingLanguage, setWritingLanguage] = useState<TranslationLanguage>('en');
  const [recipientLanguage, setRecipientLanguage] = useState<TranslationLanguage>('en');
  
  const { credits } = useUserCredits();
  const { hasInfluencerAccess: _infAccess } = useInfluencerAccess();
  const hasLegacyAccess = credits?.highest_bundle_purchased === 'legacy' || _infAccess;
  const characterCount = formData.message.length;
  const maxCharacters = 2000;

  // Live, debounced moderation so the user sees what triggered a flag
  // before they hit publish — and can jump straight to that sentence.
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [moderation, setModeration] = useState<ModerationResult | null>(null);
  useEffect(() => {
    const text = formData.message || '';
    if (!text.trim()) { setModeration(null); return; }
    const t = setTimeout(() => setModeration(moderateContent(text)), 350);
    return () => clearTimeout(t);
  }, [formData.message]);

  const flaggedSentence = moderation?.blocked ? moderation.matchedSentence : undefined;

  const focusFlaggedSentence = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta || !flaggedSentence) return;
    const idx = formData.message.toLowerCase().indexOf(flaggedSentence.toLowerCase());
    if (idx < 0) { ta.focus(); return; }
    ta.focus();
    try { ta.setSelectionRange(idx, idx + flaggedSentence.length); } catch {}
    // Scroll the selection into view
    const before = formData.message.slice(0, idx);
    const lineNum = before.split('\n').length - 1;
    const lineHeight = parseFloat(getComputedStyle(ta).lineHeight) || 24;
    ta.scrollTop = Math.max(0, lineNum * lineHeight - ta.clientHeight / 2);
  }, [flaggedSentence, formData.message]);


  // Unlock prompt: show if <20 words after 20s of inactivity
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    const wordCount = formData.message.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < 20 && !showUnlockPrompt) {
      inactivityTimer.current = setTimeout(() => {
        setShowUnlockPrompt(true);
        trackEvent('jackey_unlock_prompt_shown', { intent: intentSlug });
      }, 20000);
    } else {
      setShowUnlockPrompt(false);
    }
    return () => { if (inactivityTimer.current) clearTimeout(inactivityTimer.current); };
  }, [formData.message, intentSlug]);

  // Track intent loaded
  useEffect(() => {
    trackEvent('jackey_intent_loaded', { intent: intentSlug, relationship: formData.relationship });
  }, [intentSlug]);

  const handleVoiceTranscript = useCallback((text: string) => {
    const existing = formData.message?.trim();
    const nextMessage = existing ? `${existing}\n\n${text.trim()}` : text.trim();
    updateFormData({ message: nextMessage });
  }, [formData.message, updateFormData]);

  const handleVideoReady = useCallback((videoUrl: string) => {
    setRecordedVideoUrl(videoUrl);
    // Save the video URL in metadata so it persists through publish
    updateFormData({ metadata: { ...formData.metadata, videoUrl } as any });
    toast.success(c.videoToast);
  }, [c.videoToast, formData.metadata, updateFormData]);

  const insertStarter = (starter: string) => {
    trackEvent('jackey_sentence_starter_inserted', { intent: intentSlug, starter });
    const newMessage = formData.message ? `${formData.message}\n\n${starter}` : starter;
    updateFormData({ message: newMessage });
  };

  const handleJacKeyDismiss = () => setJacKeyDismissed(true);

  // Store language preferences in metadata
  const handleWritingLangChange = (lang: TranslationLanguage) => {
    setWritingLanguage(lang);
    trackEvent('message_language_selected', { language: lang });
    updateFormData({ metadata: { ...formData.metadata, writingLanguage: lang } as any });
  };

  const handleRecipientLangChange = (lang: TranslationLanguage) => {
    setRecipientLanguage(lang);
    trackEvent('recipient_language_selected', { language: lang });
    updateFormData({ metadata: { ...formData.metadata, recipientLanguage: lang } as any });
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
          <MessageSquare className="h-8 w-8" />
        </div>

        {/* Intent badge */}
        <div className="flex justify-center mb-3">
          <Badge variant="secondary" className="gap-1.5 text-xs px-3 py-1">
            <Sparkles className="h-3 w-3" />
            {intentLabel}
          </Badge>
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2">
          {isCrush ? c.crushTitle : c.title}
        </h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          {isCrush
            ? c.crushSubtitle
            : (<>{c.subtitle}{' '}<span className="font-medium text-foreground">{formData.recipientName || c.them}</span>.</>)
          }
        </p>
        {isCrush && (
          <p className="text-xs text-muted-foreground mt-2">{c.crushSafety}</p>
        )}

        {/* Intent intro line */}
        <p className="text-sm text-primary/80 mt-3 italic max-w-md mx-auto">
          "{lstr(intentConfig.intro, language)}"
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Language selector */}
        <MessageLanguageSelector
          writingLanguage={writingLanguage}
          recipientLanguage={recipientLanguage}
          onWritingLanguageChange={handleWritingLangChange}
          onRecipientLanguageChange={handleRecipientLangChange}
        />

        {!jacKeyDismissed && (
          <JacKeyGuideV2 formData={formData} updateFormData={updateFormData} onDismiss={handleJacKeyDismiss} />
        )}

        {/* Guided questions chips */}
        <Collapsible>
          <div className="bg-muted/30 rounded-lg border border-border/50">
            <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors rounded-lg">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                {c.questionsTitle}
              </h4>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [&[data-state=open]]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4">
              <div className="flex flex-wrap gap-2">
                {intentQuestions.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      trackEvent('jackey_question_clicked', { intent: intentSlug, questionIndex: i });
                      setShowPrompts(false);
                      toast.info(q, { duration: 5000 });
                    }}
                    className="text-xs px-3 py-2 rounded-lg bg-background border border-border hover:border-primary hover:bg-primary/5 transition-colors text-left text-muted-foreground hover:text-foreground"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Sentence starters */}
        <Collapsible>
          <div className="bg-primary/5 rounded-lg">
            <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-primary/10 transition-colors rounded-lg">
              <h4 className="font-medium text-sm">{c.starterTitle}</h4>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [&[data-state=open]]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4">
              <div className="flex flex-wrap gap-2">
                {sentenceStarters.map((phrase, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => insertStarter(phrase)}
                    className="text-xs px-3 py-1.5 rounded-full bg-background border border-border hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    {phrase}
                  </button>
                ))}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        <div className="space-y-4">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              placeholder={isCrush
                ? c.crushPlaceholder
                : `Dear ${formData.recipientName || 'someone special'}...\n\n${c.placeholder}`
              }
              value={formData.message}
              onChange={(e) => updateFormData({ message: e.target.value.slice(0, maxCharacters) })}
              className={`min-h-[250px] text-lg leading-relaxed resize-none p-4 pr-14 ${
                moderation?.blocked ? 'border-destructive focus-visible:ring-destructive/40' : ''
              }`}
            />
            {hasLegacyAccess && (
              <div className="absolute top-3 right-3">
                <VoiceInput onTranscript={handleVoiceTranscript} size="icon" className="h-8 w-8" />
              </div>
            )}
            <div className="absolute bottom-3 right-3 text-xs text-muted-foreground">
              {characterCount}/{maxCharacters}
            </div>
          </div>

          {/* Inline moderation feedback — highlights the exact sentence and lets you jump to edit it */}
          <AnimatePresence>
            {moderation?.blocked && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 space-y-3"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                  <div className="text-sm text-foreground">
                    <p className="font-medium text-destructive">
                      {moderation.reason || 'This sentence may need a small revision.'}
                    </p>
                    {moderation.suggestion && (
                      <p className="text-muted-foreground mt-1">{moderation.suggestion}</p>
                    )}
                  </div>
                </div>

                {flaggedSentence && (
                  <button
                    type="button"
                    onClick={focusFlaggedSentence}
                    className="block w-full text-left rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 hover:bg-destructive/15 transition-colors"
                  >
                    <span className="text-[11px] uppercase tracking-wide text-destructive/80">
                      Flagged sentence — tap to edit
                    </span>
                    <p className="mt-1 text-sm text-foreground">
                      <mark className="bg-destructive/20 text-foreground px-1 rounded">
                        “{flaggedSentence.length > 220 ? flaggedSentence.slice(0, 220) + '…' : flaggedSentence}”
                      </mark>
                    </p>
                  </button>
                )}

                <div className="flex justify-end">
                  <Button type="button" size="sm" variant="outline" onClick={focusFlaggedSentence}>
                    Edit this sentence
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Gentle unlock prompt on inactivity */}
          <AnimatePresence>
            {showUnlockPrompt && formData.message.trim().split(/\s+/).filter(Boolean).length < 20 && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="bg-primary/5 rounded-lg p-3 border border-primary/20 text-center"
              >
                <p className="text-sm text-primary/80 italic">{unlockPromptText}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tone adjustment tools */}
          <MessageToneTools
            message={formData.message}
            recipientName={formData.recipientName}
            relationship={formData.relationship || 'other'}
            template={formData.template}
            onMessageUpdate={(msg) => updateFormData({ message: msg })}
          />

          {formData.message && !jacKeyDismissed && (
            <p className="text-xs text-muted-foreground text-center italic">{c.editNote}</p>
          )}

          {recordedVideoUrl && (
            <div className="rounded-xl border border-primary/20 bg-muted/30 p-3 space-y-3">
              <div className="flex items-center gap-3">
                <Video className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium flex-1">{c.videoAttached}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setRecordedVideoUrl(null);
                    const nextMeta = { ...(formData.metadata as any) };
                    delete nextMeta.videoUrl;
                    updateFormData({ metadata: nextMeta });
                  }}
                  className="text-destructive hover:text-destructive"
                >
                  {c.remove}
                </Button>
              </div>
              <video
                src={recordedVideoUrl}
                controls
                playsInline
                preload="metadata"
                className="w-full rounded-lg bg-black aspect-video object-contain"
                onError={() => console.error('[MessageStep] video preview load error', { recordedVideoUrl })}
              />
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {/* Start for Me — only show when message is empty */}
            {!formData.message && (
              <StartForMe
                recipientName={formData.recipientName}
                relationship={formData.relationship || 'other'}
                template={formData.template}
                onDraftReady={(draft) => updateFormData({ message: draft })}
              />
            )}

            {hasLegacyAccess && (
              <>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowVideoRecorder(true)} className="gap-2">
                  <Video className="h-4 w-4" />
                  {c.recordVideo}
                </Button>
                <span className="text-xs text-muted-foreground flex items-center gap-1 ml-2">
                  <Mic className="h-3 w-3" />
                  {c.speakToType}
                </span>
              </>
            )}
          </div>

          <VideoRecorder open={showVideoRecorder} onOpenChange={setShowVideoRecorder} onVideoReady={handleVideoReady} maxDurationSeconds={300} />
        </div>
      </div>
    </motion.div>
  );
}
